import type { ReactNode } from "react";
import { cn } from "./cn";
import { yardimGovdesiniAyir, type YardimBlogu } from "./yardim-govde";

/**
 * YARDIM YAZISI GÖVDESİ — görsel çizim (UI/UX Dalga 2, M10; mutabakat §7)
 *
 * Düz yazı yerine yapı: menü yolu çipleri, numaralı ADIM LİSTESİ (her adım
 * tek eylem, sayı yuvarlakta), "Neden böyle" notu, ayrı "Sık karşılaşılanlar"
 * kutusu ve tıklanabilir "İlgili yazılar". Ayrıştırma `yardim-govde.ts`te
 * (saf, test edilebilir); burası yalnız çiziyor ve satır DÜŞÜRMÜYOR.
 *
 * Panel ve öğrenci/veli uygulaması AYNI bileşeni kullanıyor: ikinci bir kopya
 * eskirdi. Renkler `tokens.css` belirteçlerinden; dar ekranda tek sütun.
 */
export function YardimGovdesi({
  govde,
  onIlgili,
  ilgiliBulunur,
  eylem,
  className,
}: {
  govde: string;
  /** İlgili yazı başlığına basılınca. Verilmezse başlıklar düz metin. */
  onIlgili?: (baslik: string) => void;
  /** Başlığın gerçekten açılabilir bir yazıya karşılık gelip gelmediği. */
  ilgiliBulunur?: (baslik: string) => boolean;
  /** Yazının sonundaki eylem (ör. "Ekrana git"). */
  eylem?: ReactNode;
  className?: string;
}) {
  const bloklar = yardimGovdesiniAyir(govde);
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {bloklar.map((b, i) => (
        <Blok key={i} blok={b} onIlgili={onIlgili} ilgiliBulunur={ilgiliBulunur} />
      ))}
      {eylem && <div>{eylem}</div>}
    </div>
  );
}

function Blok({
  blok,
  onIlgili,
  ilgiliBulunur,
}: {
  blok: YardimBlogu;
  onIlgili?: (baslik: string) => void;
  ilgiliBulunur?: (baslik: string) => boolean;
}) {
  switch (blok.tur) {
    case "paragraf":
      return (
        <p className="text-notr-700 text-sm leading-relaxed whitespace-pre-line">{blok.metin}</p>
      );

    case "yol":
      return (
        <nav aria-label="Menü yolu" className="flex flex-wrap items-center gap-1.5 text-[13px]">
          <span className="text-notr-600 font-medium">Menüden:</span>
          <ol className="flex flex-wrap items-center gap-1.5">
            {blok.adimlar.map((a, i) => (
              <li key={`${a}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span aria-hidden="true" className="text-notr-400">
                    ›
                  </span>
                )}
                <span className="rounded-alan bg-marka-50 text-marka-700 px-2 py-0.5 font-medium">
                  {a}
                </span>
              </li>
            ))}
          </ol>
        </nav>
      );

    case "adimlar":
      return (
        <ol className="flex flex-col gap-2.5">
          {blok.adimlar.map((a, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="bg-marka-600 text-notr-0 grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold"
              >
                {i + 1}
              </span>
              <span className="text-notr-800 pt-0.5 text-sm leading-relaxed">{a}</span>
            </li>
          ))}
        </ol>
      );

    case "neden":
      return (
        <p className="border-marka-200 bg-marka-50 text-notr-700 rounded-alan border-l-4 px-3 py-2 text-sm leading-relaxed">
          <strong className="text-marka-700 font-semibold">Neden böyle: </strong>
          {blok.metin}
        </p>
      );

    case "siklar":
      return (
        <section
          aria-label="Sık karşılaşılanlar"
          className="border-uyari-200 bg-uyari-50 rounded-kart border p-3.5"
        >
          <h4 className="text-uyari-800 mb-2 text-[13px] font-semibold">Sık karşılaşılanlar</h4>
          <ul className="flex flex-col gap-2">
            {blok.maddeler.map((m, i) => (
              <li key={i} className="text-notr-800 text-sm leading-relaxed">
                <span className="font-medium">{m.sorun}</span>
                {m.cozum !== null && (
                  <>
                    <span aria-hidden="true" className="text-notr-600">
                      {" → "}
                    </span>
                    <span className="sr-only"> Çözüm: </span>
                    <span className="text-notr-700">{m.cozum}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      );

    case "ilgili":
      return (
        <section aria-label="İlgili yazılar" className="flex flex-wrap items-center gap-2">
          <span className="text-notr-600 text-[13px] font-medium">İlgili yazılar:</span>
          {blok.basliklar.map((baslik) =>
            onIlgili && (ilgiliBulunur?.(baslik) ?? true) ? (
              <button
                key={baslik}
                type="button"
                onClick={() => onIlgili(baslik)}
                className="rounded-rozet text-marka-700 ring-marka-200 hover:bg-marka-50 min-h-dokunma px-3 text-[13px] font-medium ring-1 ring-inset"
              >
                {baslik}
              </button>
            ) : (
              <span key={baslik} className="text-notr-700 text-[13px]">
                {baslik}
              </span>
            ),
          )}
        </section>
      );
  }
}
